import { Component } from '@theme/component';
import './slideshow.js';

/** Tinker owns all slide navigation; this wrapper only gates optional autoplay. */
class CdailyHomeHero extends Component {
  #abort;
  #motion;
  #mobile;
  #userPaused = false;
  #mediaObserver;
  #mediaTimer;
  #visibilityObserver;
  #visibleEnough = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#mediaObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    clearTimeout(this.#mediaTimer);
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#mobile = matchMedia('(max-width: 749px)');
    // Until the first observation, keep Tinker's existing timer suspended.
    this.#visibleEnough = typeof IntersectionObserver !== 'function';
    if (!this.#visibleEnough) {
      this.#visibilityObserver = new IntersectionObserver(([entry]) => {
        if (signal.aborted) return;
        const visibleEnough = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        if (visibleEnough === this.#visibleEnough) return;
        this.#visibleEnough = visibleEnough;
        this.#syncPlayback();
      }, { threshold: [0, 0.25] });
      this.#visibilityObserver.observe(this);
    }
    this.#prepareMedia();
    this.#motion.addEventListener('change', this.#syncPlayback, { signal: this.#abort.signal });
    this.#mobile.addEventListener('change', this.#syncPlayback, { signal: this.#abort.signal });
    document.addEventListener('visibilitychange', this.#syncPlayback, { signal: this.#abort.signal });
    for (const name of ['focusin', 'focusout', 'mouseenter', 'mouseleave']) {
      this.addEventListener(name, () => queueMicrotask(this.#syncPlayback), { signal: this.#abort.signal });
    }
    this.addEventListener('slideshow:select', this.#afterSelect, { signal: this.#abort.signal });
    this.addEventListener('click', (event) => {
      if (event.target.closest('.icon-pause')) this.#userPaused = true;
      if (event.target.closest('.icon-play')) this.#userPaused = false;
      queueMicrotask(this.#syncPlayback);
    }, { signal: this.#abort.signal });
    customElements.whenDefined('slideshow-component').then(() => {
      if (!signal.aborted && this.isConnected) this.#syncPlayback();
    });
  }

  #loadMedia = (slide) => {
    const template = slide?.querySelector('template[data-cdaily-hero-media]');
    if (!template) return;
    const content = template.content.cloneNode(true);
    const image = content.querySelector('img');
    if (image) {
      image.loading = 'eager';
      image.fetchPriority = 'low';
    }
    template.replaceWith(content);
  };

  #queueNextMedia = (index = 0) => {
    clearTimeout(this.#mediaTimer);
    const autoplay = this.#mobile.matches ? this.dataset.mobileRotate === 'true' : this.dataset.rotate === 'true';
    if (!autoplay || this.#motion.matches) return;
    this.#mediaTimer = setTimeout(() => {
      const slides = this.querySelectorAll('.cdaily-home__hero-slide');
      this.#loadMedia(slides[(index + 1) % slides.length]);
    }, 2000);
  };

  #prepareMedia() {
    const slides = this.querySelectorAll('.cdaily-home__hero-slide');
    const prepareInteraction = () => slides.forEach(this.#loadMedia);
    for (const type of ['pointerdown', 'keydown']) {
      this.addEventListener(type, prepareInteraction, { signal: this.#abort.signal, passive: true });
    }
    if (typeof IntersectionObserver === 'function') {
      this.#mediaObserver = new IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting) this.#loadMedia(entry.target);
      }, { root: this.querySelector('slideshow-slides'), threshold: 0.01 });
      slides.forEach(slide => this.#mediaObserver.observe(slide));
    }
    const first = slides[0]?.querySelector('img');
    if (first?.complete) this.#queueNextMedia();
    else first?.addEventListener('load', () => this.#queueNextMedia(), { once: true, signal: this.#abort.signal });
  }

  #syncPlayback = () => {
    if (!this.isConnected) return;
    const slider = this.querySelector('slideshow-component');
    if (!slider) return;
    this.toggleAttribute('data-motion-reduced', this.#motion.matches);
    if (this.#motion.matches) {
      for (const slide of this.querySelectorAll('.cdaily-home__hero-slide')) {
        for (const animation of slide.getAnimations()) animation.cancel();
      }
    }
    const autoplay = this.#mobile.matches ? this.dataset.mobileRotate === 'true' : this.dataset.rotate === 'true';
    const multipleSlides = this.querySelectorAll('.cdaily-home__hero-slide').length > 1;
    this.toggleAttribute('data-playback-disabled', !autoplay || !multipleSlides || this.#motion.matches);
    if (!autoplay || !multipleSlides || this.#motion.matches || document.hidden || !this.#visibleEnough) {
      slider.suspend();
      // Prevent native hover/visibility resume from bypassing this gate.
      slider.removeAttribute('autoplay');
      return;
    }
    slider.setAttribute('autoplay', this.dataset.interval || '6');
    if (this.matches(':focus-within') || (matchMedia('(hover: hover)').matches && this.matches(':hover'))) {
      slider.suspend();
      return;
    }
    // Enabling motion never overrides a visitor's explicit pause.
    if (!this.#userPaused) slider.play();
  };

  #afterSelect = (event) => {
    if (event.target !== this.querySelector('slideshow-component')) return;
    this.#loadMedia(event.detail?.slide);
    this.#queueNextMedia(event.detail?.index ?? 0);
    // Native Tinker owns scrolling/swiping. Add a small desktop image fade only.
    if (!this.#motion.matches && !this.#mobile.matches && event.detail?.slide && event.detail.trigger !== 'scroll') {
      event.detail.slide.querySelector('.cdaily-home__hero-slide')?.animate(
        [{ opacity: .65 }, { opacity: 1 }], { duration: 500, easing: 'ease-out' }
      );
    }
    const slider = this.querySelector('slideshow-component');
    slider?.suspend();
    this.#syncPlayback();
  };

  disconnectedCallback() {
    this.#abort?.abort();
    this.#mediaObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    clearTimeout(this.#mediaTimer);
    this.querySelector('slideshow-component')?.pause();
    super.disconnectedCallback();
  }
}

if (!customElements.get('cdaily-home-hero')) customElements.define('cdaily-home-hero', CdailyHomeHero);
